import React, { useMemo } from 'react';

import {
  GraphSettingsSlug,
  graphSettingsRouteConfig,
} from 'src/app/graph/routes';
import { L3SettingsMenu } from 'src/components/l3SettingsMenu/L3SettingsMenu';
import { useCapabilities } from 'src/hooks/useCapabilities';
import { useRouteParams } from 'src/hooks/useRouteParams';

import { GraphRef } from '../hooks/useGraphRef';

export const GraphSettingsMenu = ({
  graphRef,
  hasSupergraphVariants,
}: {
  graphRef: GraphRef;
  hasSupergraphVariants: boolean;
}) => {
  const { tab } = useRouteParams(graphSettingsRouteConfig);
  const { capabilities } = useCapabilities();

  const applyRouteConfig = (tabName: GraphSettingsSlug) => {
    return graphSettingsRouteConfig.location({ ...graphRef, tab: tabName });
  };

  const graphSettingsMenuItems = useMemo(
    (): React.ComponentProps<
      typeof L3SettingsMenu<GraphSettingsSlug>
    >['settingsMenuItems'] => [
      { slug: 'general', title: 'General' },
      { slug: 'permissions', title: 'Permissions' },
      { slug: 'api-keys', title: 'API Keys' },
      { slug: 'reporting', title: 'Reporting' },
      { slug: 'variants', title: 'Variants' },
      ...(capabilities?.proposals && hasSupergraphVariants
        ? ([{ slug: 'proposals', title: 'Proposals' }] as const)
        : []),
      {
        slug: 'checks',
        title: 'Checks',
        defaultNested: 'checks_operations',
        nested: [
          { slug: 'checks_operations', title: 'Operations' },
          { slug: 'checks_linter', title: 'Linter' },
          ...(capabilities?.proposals
            ? [{ slug: 'checks_proposals' as const, title: 'Proposals' }]
            : []),
          ...(capabilities?.webhooks
            ? [
                {
                  slug: 'checks_custom_checks' as const,
                  title: 'Custom checks',
                },
              ]
            : []),
        ],
      },
    ],
    [hasSupergraphVariants, capabilities],
  );

  return (
    <L3SettingsMenu
      settingsMenuItems={graphSettingsMenuItems}
      applyRouteConfig={applyRouteConfig}
      defaultSlug="general"
      currentTab={tab}
    />
  );
};
